import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserProfile } from './types';
import { verifySsoTokenApi, apiClient } from './services/apiService';

export const hasActiveSession = (): boolean => {
  if (typeof document === 'undefined') return false;
  return document.cookie.split('; ').some(row => row.startsWith('ngs_session_status=active'));
};

export const checkSSOCookieStatus = hasActiveSession;

export interface AuthContextType {
  user: UserProfile | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isMonetizationEnabled: boolean;
  setIsMonetizationEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setUser: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  logout: () => Promise<void>;
  localLogout: () => void;
  checkSSOCookieStatus: () => boolean;
  hasActiveSession: () => boolean;
  checkAuthSession: () => Promise<void>;
  refetchProfile: () => Promise<void>;
  updateCredits: (newAmount: number) => void;
  loginSuccess: (newUser: UserProfile) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [isMonetizationEnabled, setIsMonetizationEnabled] = useState<boolean>(true);

  // TanStack Query fetching from /api/sso/exchange
  const { data: userAuthData, isLoading, refetch, isError } = useQuery({
    queryKey: ['authSession'],
    queryFn: async () => {
      // Check cookie first before making API call to save network if already logged out
      if (!hasActiveSession()) return null;
      const res = await verifySsoTokenApi();
      if (res.valid && res.user) return res.user;
      return null;
    },
    refetchOnWindowFocus: false, // Tắt cờ tự động gọi lại quá nhạy của React Query
    retry: false,
    staleTime: 5 * 60 * 1000 // 5 mins
  });

  const user = userAuthData || null;

  // Manual state updater to keep compatibility with existing components
  const setUserState = useCallback((val: UserProfile | null | ((prev: UserProfile | null) => UserProfile | null)) => {
    if (typeof val === 'function') {
      queryClient.setQueryData(['authSession'], (old: UserProfile | null) => val(old));
    } else {
      queryClient.setQueryData(['authSession'], val);
    }
  }, [queryClient]);

  // Fetch GET /api/config 1 lần lúc khởi động App để lấy giá trị isMonetizationEnabled
  useEffect(() => {
    let isMounted = true;
    const fetchAppConfig = async () => {
      // Wait for auth to finish? "Chặn tuyệt đối việc gọi các API cấu hình chung khi state user chưa có dữ liệu."
      if (!user) return;
      try {
        const res = await apiClient.get('/config');
        if (res.data) {
          const configObj = res.data.config || res.data.data;
          if (configObj && typeof configObj.isMonetizationEnabled === 'boolean') {
            if (isMounted) setIsMonetizationEnabled(configObj.isMonetizationEnabled);
          }
        }
      } catch (err) {
        console.warn('Could not load system config at startup:', err);
      }
    };

    fetchAppConfig();

    const handleConfigUpdated = (e: CustomEvent) => {
      if (e.detail && typeof e.detail.isMonetizationEnabled === 'boolean') {
        setIsMonetizationEnabled(e.detail.isMonetizationEnabled);
      } else {
        fetchAppConfig();
      }
    };

    window.addEventListener('app_config_updated' as any, handleConfigUpdated as any);
    return () => {
      isMounted = false;
      window.removeEventListener('app_config_updated' as any, handleConfigUpdated as any);
    };
  }, [user]);

  // Dùng Ref để tránh trường hợp closure bị cũ trong event listeners và interval
  const userRef = useRef<UserProfile | null>(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const localLogout = useCallback(() => {
    setUserState(null);
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('ngs_last_redirect');
    }
  }, [setUserState]);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/sso/logout');
    } catch (err) {
      console.warn('Backend logout API error:', err);
    } finally {
      localLogout();
      // Đưa URL về trang chủ / đăng nhập và reset tab về Hồ sơ
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('ngs_last_redirect');
        window.location.href = '/';
      }
    }
  }, [localLogout]);

  const checkAuthSession = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const loginSuccess = useCallback(async (newUser: UserProfile) => {
    setUserState(newUser);
    // Refresh to get full data from backend if needed
    await refetch();
  }, [setUserState, refetch]);

  // Lắng nghe visibilitychange hoặc polling chu kỳ chậm (3s)
  const checkSession = useCallback(() => {
    if (userRef.current !== null && !hasActiveSession()) {
      localLogout();
      // Đá ra màn hình login
      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    }
  }, [localLogout]);

  useEffect(() => {
    const handleFocus = () => checkSession();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSession();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkSession]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      checkSession();
    }, 3000);

    return () => clearInterval(intervalId);
  }, [checkSession]);

  // Nghe sự kiện auth_unauthorized từ apiService để đá văng ra ngoài
  useEffect(() => {
    const handleUnauthorized = () => {
      localLogout();
      window.location.href = '/';
    };
    window.addEventListener('auth_unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth_unauthorized', handleUnauthorized);
  }, [localLogout]);

  const updateCredits = useCallback((newAmount: number) => {
    setUserState(prev => prev ? { ...prev, credits: newAmount } : null);
  }, [setUserState]);

  const refetchProfile = useCallback(async () => {
    await checkAuthSession();
  }, [checkAuthSession]);

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile: user,
        loading: isLoading,
        isMonetizationEnabled,
        setIsMonetizationEnabled,
        setUser: setUserState,
        setUserProfile: setUserState,
        logout,
        localLogout,
        checkSSOCookieStatus,
        hasActiveSession,
        checkAuthSession,
        refetchProfile,
        updateCredits,
        loginSuccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
